import React, { FC, memo, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid, Typography, Button } from '@mui/material'
import { toast } from 'react-toastify'
import { InternalDocumentTypesEnum, UploadKycFileResponseDto } from '@sparling/financial-common'
import { useStyles } from '../useStyles'
import { useRefresh, useRest } from '../../../../components/Context'
import { InputSelect } from '../../../../components/InputSelect'
import { Modal } from '../../../../components/Modal'
import { createEnum } from '../../../../utils/createEnum'
import { InputField } from '../../../../components/InputField'
import { uploadKycFileResolver } from './resolvers'

interface IUploadFile {
  isOpen: boolean
  modalClose: () => void
  idSf: string
}

type FormInputs = {
  docType: InternalDocumentTypesEnum
  file: File
  comment: string
}

const UploadFile: FC<IUploadFile> = ({ isOpen, modalClose, idSf }) => {
  const formMethods = useForm<FormInputs>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    resolver: uploadKycFileResolver,
  })
  const classes = useStyles()
  const { rxiosUpload } = useRest()
  const { saveIsRefresh } = useRefresh()
  const {
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { isValid },
  } = formMethods
  const [loading, setLoading] = useState(false)

  const onSubmit = useCallback(() => {
    setLoading(true)
    const data = getValues()
    if (!data.file) {
      setLoading(false)
      return
    }
    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('docType', data.docType)
    formData.append('comment', data.comment)

    rxiosUpload.post<UploadKycFileResponseDto>(`/gcs/upload/${idSf}`, formData).subscribe({
      next: (response) => {
        if (response.success) {
          reset()
          modalClose()
          saveIsRefresh(true)
          toast.success('File uploaded successfully')
          setLoading(false)
        }
      },
      error: (err) => {
        toast.error(err?.response?.data.message)
        setLoading(false)
      },
    })
  }, [getValues, idSf, rxiosUpload, reset, modalClose, saveIsRefresh])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentFile = e.target.files?.[0]
      if (currentFile) setValue('file', currentFile, { shouldValidate: true })
    },
    [setValue],
  )

  useEffect(() => {
    if (!isOpen) setTimeout(reset, 300)
  }, [isOpen, reset])

  return (
    <Modal
      title="Add Document"
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(onSubmit)}
      submitTitle={'Upload'}
      isSubmitDisabled={!isValid || loading}
    >
      <FormProvider {...formMethods}>
        <Grid container>
          <Grid item xs={12} className={classes.field}>
            <Typography component="span">Document Type: </Typography>
            <InputSelect
              enums={createEnum(InternalDocumentTypesEnum)}
              width={200}
              inputName="docType"
              placeholder="Please select"
            />
          </Grid>
          <Grid item xs={12} className={classes.field}>
            <Typography component="span">Upload Document: </Typography>
            <Button className={classes.buttonWidth} color="primary" variant="contained" component="label">
              {watch().file?.name || 'Choose File'}
              <input type="file" accept="image/jpeg" name="file" hidden onChange={handleFileChange} required />
            </Button>
          </Grid>
          <Grid item xs={12} className={classes.field}>
            <Typography component="span">Comment: </Typography>
            <InputField inputName="comment" inputType="text" maximumHeight={'auto'} multiline rows={4} withoutPadding />
          </Grid>
        </Grid>
      </FormProvider>
    </Modal>
  )
}

export const MemoUploadFile = memo(UploadFile)
